import { IconCheckCircle } from "@/assets/icons/geist/IconCheckCircle";
import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import {
    useCreateSSOConfiguration,
    useDeleteSSOConfiguration,
    useUpdateSSOConfiguration,
} from "@/queries/organization-queries";
import { isValidHTTPSURL } from "@/utils/url-validation";
import { formatError } from "@/utils/errors";
import {
    CreateSSOConfigurationRequest,
    UpdateSSOConfigurationRequest,
    type SSOConfiguration,
} from "gitpod-next-api/gitpod/v1/organization_pb";
import type { PartialMessage } from "@bufbuild/protobuf";
import { useCallback, useMemo, useState, type FC, type FormEvent } from "react";
import { useForm } from "react-hook-form";

type SSOConfigurationWithDisplayName = SSOConfiguration & {
    displayName?: string;
};

export type ConfigureSSOProps = {
    organizationId: string;
    config?: SSOConfigurationWithDisplayName;
    onClose?: () => void;
    onVerify?: () => void;
    testValidationOnly?: boolean;
};

type SSOForm = {
    display_name: string;
    issuer_url: string;
    client_id: string;
    client_secret: string;
};

type DisplayNamePayload = {
    displayName?: string;
};

type ConfigureSSOState = "configure" | "confirm" | "remove";

export const ConfigureSSOV2: FC<ConfigureSSOProps> = ({
    organizationId,
    config,
    onClose,
    onVerify: _onVerify,
    testValidationOnly,
}) => {
    const { toast } = useToast();
    const [state, setState] = useState<ConfigureSSOState>("configure");

    const isEdit = !!config;

    const {
        register,
        handleSubmit,
        formState: { errors, isValid, isSubmitting, dirtyFields, isDirty },
        getValues,
    } = useForm<SSOForm>({
        mode: "all",
        defaultValues: isEdit
            ? {
                  display_name: config.displayName ?? "",
                  issuer_url: config.issuerUrl,
                  client_id: config.clientId,
                  client_secret: config.id,
              }
            : undefined,
    });

    const createSSOConfig = useCreateSSOConfiguration();
    const updateSSOConfig = useUpdateSSOConfiguration();
    const deleteSSOConfig = useDeleteSSOConfiguration();

    const [serverError, setServerError] = useState<string>();

    const handleServerErrors = useCallback(
        (error: unknown, _data: SSOForm) => {
            const description = formatError(error);
            setServerError(description);
            toast({
                title: "Failed to create SSO configuration",
                description,
            });
        },
        [toast],
    );

    const handleOnSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            const onValid = async (data: SSOForm) => {
                if (testValidationOnly) {
                    console.warn("Test validation only, not submitting form.");
                    return;
                }
                setServerError(undefined);
                let ssoId = "";
                try {
                    if (isEdit) {
                        ssoId = config.id;
                        const updatePayload: PartialMessage<UpdateSSOConfigurationRequest> & DisplayNamePayload = {
                            ssoConfigurationId: config.id,
                            displayName: dirtyFields.display_name ? data.display_name : undefined,
                            clientId: dirtyFields.client_id ? data.client_id : undefined,
                            issuerUrl: dirtyFields.issuer_url ? data.issuer_url : undefined,
                            clientSecret: dirtyFields.client_secret ? data.client_secret : undefined,
                        };
                        await updateSSOConfig.mutateAsync(new UpdateSSOConfigurationRequest(updatePayload));
                    } else {
                        const createPayload: PartialMessage<CreateSSOConfigurationRequest> & DisplayNamePayload = {
                            organizationId,
                            displayName: data.display_name,
                            issuerUrl: data.issuer_url,
                            clientId: data.client_id,
                            clientSecret: data.client_secret,
                        };
                        const result = await createSSOConfig.mutateAsync(
                            new CreateSSOConfigurationRequest(createPayload),
                        );
                        ssoId = result.id;
                    }
                } catch (error) {
                    handleServerErrors(error, data);
                    return;
                }
                window.open(`/auth/oidc/start?ssoId=${ssoId}&verify=true`, "_blank");
                setState("confirm");
            };
            await handleSubmit(onValid)(event);
        },
        [
            handleSubmit,
            testValidationOnly,
            isEdit,
            config?.id,
            updateSSOConfig,
            dirtyFields,
            createSSOConfig,
            organizationId,
            handleServerErrors,
        ],
    );

    const callbackURL = useMemo(() => {
        return new URL("/auth/oidc/callback", window.location.origin).toString();
    }, []);

    const onOpenDiscoveryMetadata = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            const url = getValues("issuer_url");
            if (!url) {
                return;
            }
            window.open(`${url}/.well-known/openid-configuration`, "_blank");
        },
        [getValues],
    );

    const onRemove = useCallback(async () => {
        if (!config?.id) {
            return;
        }
        try {
            await deleteSSOConfig.mutateAsync(config.id);
            toast({
                title: "SSO configuration removed",
                description: "The SSO configuration has been removed.",
            });
            onClose?.();
        } catch (error) {
            toast({
                title: "Failed to remove SSO configuration",
                description: formatError(error),
            });
        }
    }, [config?.id, deleteSSOConfig, onClose, toast]);

    if (state === "confirm") {
        return (
            <div>
                <Header />

                <DialogBody className="flex flex-col gap-2">
                    <div className="flex flex-col items-center gap-2 rounded-xl border-[0.5px] bg-surface-primary px-6 py-3 text-base text-content-tertiary">
                        <Heading2>SSO configured successfully!</Heading2>
                        <Text>The authentication test should have opened a new browser tab.</Text>
                        <IconCheckCircle size="lg" className="size-16 text-green-500" />
                        <Text>
                            Add and verify the email domains you want to use for login, or use existing verified
                            domains.
                        </Text>
                    </div>
                </DialogBody>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setState("configure")}
                        data-tracking-id="edit-configure-sso-v2"
                    >
                        Edit
                    </Button>
                    <div className="flex-grow" />
                    <Button variant="primary" autoFocus={true} onClick={onClose} data-tracking-id="confirm-sso-config">
                        OK
                    </Button>
                </DialogFooter>
            </div>
        );
    }
    if (state === "remove") {
        return (
            <div>
                <DialogHeader className="flex flex-col gap-2">
                    <DialogTitle>Remove SSO configuration</DialogTitle>
                </DialogHeader>

                <DialogBody className="flex flex-col gap-2">
                    <Text>
                        This will delete your SSO configuration. Members will no longer be able to log in using SSO.{" "}
                    </Text>
                    <Text>Are you sure you&apos;d like to delete this configuration?</Text>
                </DialogBody>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => setState("configure")}
                        data-tracking-id="cancel-delete-sso-configure-sso"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={onRemove}
                        data-tracking-id="confirm-delete-sso-configure-sso"
                    >
                        Yes, Remove
                    </Button>
                </DialogFooter>
            </div>
        );
    }

    // the default state is "configure"
    return (
        <div>
            <Header />
            <DialogBody className="my-8 flex justify-between gap-3 overflow-y-auto py-0">
                <form
                    onSubmit={handleOnSubmit}
                    id={"setup-sso-form"}
                    name={"Setup SSO"}
                    className="flex w-full flex-col gap-4"
                >
                    <Text>
                        Find instructions for setting up an OIDC application in the{" "}
                        <ExternalLink href="https://ona.com/docs/ona/sso/overview">documentation</ExternalLink>.
                    </Text>
                    {serverError && (
                        <Banner
                            variant="danger"
                            text={
                                <>
                                    <div className="font-bold">Failed to update the SSO configuration</div>
                                    <div className="text-sm">{serverError}</div>
                                </>
                            }
                        />
                    )}
                    <InputField
                        label="Display name"
                        id="display_name"
                        hint="This name appears on the selection screen when multiple SSO options are available."
                        error={errors?.display_name?.message || ""}
                    >
                        <Input
                            id="display_name"
                            placeholder="Acme Inc."
                            type="text"
                            {...register("display_name", {
                                validate: (value) =>
                                    value.trim().length >= 3 || "Display name must be at least 3 characters.",
                                required: {
                                    value: true,
                                    message: "Display name is required.",
                                },
                                maxLength: {
                                    value: 64,
                                    message: "Display name must be 64 characters or fewer.",
                                },
                            })}
                        />
                    </InputField>
                    <InputField
                        label="Issuer URL"
                        id="issuer_url"
                        hint={
                            !errors?.issuer_url?.message && (
                                <span>
                                    Check the{" "}
                                    <ExternalLink
                                        href={``}
                                        className="text-sm"
                                        onClick={onOpenDiscoveryMetadata}
                                        data-tracking-id="open-oidc-discovery-metadata-configure-sso"
                                    >
                                        OIDC Discovery metadata
                                    </ExternalLink>{" "}
                                    of your identity provider.
                                </span>
                            )
                        }
                        error={errors?.issuer_url?.message || ""}
                    >
                        <Input
                            id="issuer_url"
                            placeholder="https://sso.example.com/oauth2/default"
                            type="text"
                            {...register("issuer_url", {
                                validate: (value) =>
                                    isValidHTTPSURL(value) || "Must be a valid URL starting with https://.",
                                required: {
                                    value: true,
                                    message: "Issuer URL is required.",
                                },
                            })}
                        />
                    </InputField>
                    <InputField label="Client ID" id="client_id" error={errors?.client_id?.message || ""}>
                        <Input
                            id="client_id"
                            type="text"
                            {...register("client_id", {
                                required: {
                                    value: true,
                                    message: "Client ID is required.",
                                },
                            })}
                        />
                    </InputField>
                    <InputField label="Client secret" id="client_secret" error={errors?.client_secret?.message || ""}>
                        <Input
                            id="client_secret"
                            type="password"
                            {...register("client_secret", {
                                required: {
                                    value: true,
                                    message: "Client secret is required.",
                                },
                            })}
                        />
                    </InputField>
                    <InputField
                        label="Callback URL"
                        id="callback_url"
                        hint="Copy and paste this URL into the OIDC app of your identity provider."
                    >
                        <CopyableInput
                            id="callback_url"
                            readOnly
                            value={callbackURL}
                            title="Copy callback URL"
                            data-tracking-id="copy-callback-url-configure-sso"
                            disabled
                        />
                    </InputField>
                </form>
            </DialogBody>
            <DialogFooter className="flex flex-row">
                {isEdit && (
                    <Button
                        variant="destructive"
                        onClick={() => setState("remove")}
                        data-tracking-id="remove-sso-config-configure-sso"
                    >
                        Remove
                    </Button>
                )}
                <div className="flex-grow" />
                <DialogClose asChild>
                    <Button variant="outline" onClick={onClose} data-tracking-id="cancel-sso-config-configure-sso">
                        Cancel
                    </Button>
                </DialogClose>
                <Button
                    autoFocus={true}
                    aria-label="test-and-continue"
                    type="submit"
                    data-testid="test-and-continue-button"
                    form="setup-sso-form"
                    variant="primary"
                    loading={isSubmitting}
                    disabled={!isValid || !(isDirty || isEdit)}
                    data-tracking-id="save-sso-config-configure-sso"
                >
                    Save & Test
                </Button>
            </DialogFooter>
        </div>
    );
};

const Header: FC = () => {
    return (
        <DialogHeader className="flex flex-col gap-2">
            <DialogTitle>Configure SSO</DialogTitle>
            <DialogDescription>
                Configure OpenID Connect (OIDC) Single Sign-On with your identity provider.
            </DialogDescription>
        </DialogHeader>
    );
};
